import { useState, type FormEvent } from 'react';

import { voidGiftPass } from '../../api/gift-pass-actions';
import { Field } from '../dashboards/primitives/Field';
import { fieldStyle } from '../dashboards/primitives/field-style';
import { SimpleModal } from '../shared/SimpleModal';

import { actionErrorMessage, voidReasonError } from './gift-pass-actions-model';

export interface GiftPassVoidModalProps {
  passId: string;
  onClose: () => void;
  onVoided: () => void;
}

export function GiftPassVoidModal({ passId, onClose, onVoided }: GiftPassVoidModalProps) {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const found = voidReasonError(reason);
    setReasonError(found);
    setSubmitError(null);
    if (found) return;
    setSubmitting(true);
    try {
      await voidGiftPass(passId, reason.trim());
      onVoided();
    } catch (err) {
      setSubmitError(actionErrorMessage(err, 'Something went wrong and the pass was not voided.'));
      setSubmitting(false);
    }
  };

  return (
    <SimpleModal
      open
      onClose={() => {
        if (!submitting) onClose();
      }}
      title="Void this gift pass?"
    >
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <p style={{ margin: 0, fontSize: 13 }}>
          The code stops working straight away and any send still waiting to go out is cancelled.
          Voiding cannot be undone. If you void by mistake, create a comp pass instead.
        </p>
        <Field label="Reason" required error={reasonError ?? undefined}>
          <textarea
            value={reason}
            rows={3}
            onChange={(e) => setReason(e.target.value)}
            style={fieldStyle(reasonError ? 'error' : undefined, {
              height: 'auto',
              padding: '8px 10px',
            })}
          />
        </Field>
        {submitError && (
          <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--nb-risk)' }}>
            {submitError}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            className="nb-btn nb-btn-ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Keep pass
          </button>
          <button type="submit" className="nb-btn gp-btn-danger" disabled={submitting}>
            {submitting ? 'Voiding…' : 'Void pass'}
          </button>
        </div>
      </form>
    </SimpleModal>
  );
}
